/**
 * Location-related data fetchers
 */
import { QueryClient, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { createOrUpdateLocation, deleteLocation } from "@/src/actions/location";
import { toast } from "@/src/hooks/use-toast";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import { TASKS_CACHE_KEY, TASK_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Location } from "@/src/models/location";
import { getAllLocations, getLocationById } from "@/src/requests/location";

export const LOCATIONS_CACHE_KEY = "locations";

export const updateLocationsQueryData = (queryClient: QueryClient, updatedLocation: Location) => {
  queryClient.setQueryData<Location[]>([LOCATIONS_CACHE_KEY], (oldData: Location[] = []) => {
    const current = oldData;
    const exists = current.some((loc) => loc.id === updatedLocation.id);

    if (exists) {
      return current.map((loc) => (loc.id === updatedLocation.id ? updatedLocation : loc));
    }

    return [updatedLocation, ...current];
  });

  // Also update the individual location cache if it exists
  if (updatedLocation.id) {
    queryClient.setQueryData([LOCATIONS_CACHE_KEY, updatedLocation.id], updatedLocation);
  }
};

export function useLocations() {
  const query = useQuery({
    queryKey: [LOCATIONS_CACHE_KEY],
    queryFn: async () => {
      try {
        const locations = await getAllLocations();

        return locations || [];
      } catch (error) {
        logger.error("Error in useLocations:");
        throw error;
      }
    },
    retry: false,
    staleTime: Infinity,
  });

  return {
    locations: query.data || [],
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useLocationById(locationId: string, options?: { enabled?: boolean }) {
  const {
    data: location,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [LOCATIONS_CACHE_KEY, locationId],
    queryFn: () => getLocationById(locationId),
    staleTime: Infinity,
    enabled: options?.enabled !== false && !!locationId,
  });

  return { location, isFetching, isLoading, isError };
}

export function useLocationCreateOrUpdateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (location: Location) => {
      const result = await createOrUpdateLocation(location, true);

      if (!result) {
        throw new Error("Failed to update location");
      }

      return result;
    },
    onMutate: async (location) => {
      await queryClient.cancelQueries({ queryKey: [LOCATIONS_CACHE_KEY] });

      const previousLocations = queryClient.getQueryData<Location[]>([LOCATIONS_CACHE_KEY]) ?? [];

      // Optimistically update the locations cache
      updateLocationsQueryData(queryClient, location);

      return { previousLocations };
    },
    onSuccess: (savedLocation) => {
      // Ensure the optimistic entry is reconciled with the saved server result
      // This updates both the list cache and individual location cache
      updateLocationsQueryData(queryClient, savedLocation);
    },
    onError: (error, variables, context) => {
      logger.error("Error updating location");

      if (context) {
        queryClient.setQueryData([LOCATIONS_CACHE_KEY], context.previousLocations);
      }

      toast({
        title: "Failed to save location",
        description: "There was an error saving the location. Please try again.",
        variant: "destructive",
      });
    },
    meta: {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]],
    },
  });
}

export function useDeleteLocationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ locationId }: { locationId: string }) => {
      return deleteLocation({ locationId });
    },
    onMutate: async ({ locationId }) => {
      await queryClient.cancelQueries({ queryKey: [LOCATIONS_CACHE_KEY] });

      const previousLocations = queryClient.getQueryData<Location[]>([LOCATIONS_CACHE_KEY]) ?? [];

      queryClient.setQueryData([LOCATIONS_CACHE_KEY], (locations: Location[] = []) => {
        return locations.filter((loc) => loc.id !== locationId);
      });

      return { previousLocations };
    },
    onError: (error, { locationId }, context) => {
      if (context) {
        queryClient.setQueryData([LOCATIONS_CACHE_KEY], context.previousLocations);
      }

      logger.error("Error deleting location:", {
        locationId,
        error,
      });

      toast({
        title: "Location deletion failed",
        description: "There was an error deleting the location. Please try again.",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCATIONS_CACHE_KEY] });
      toast({
        title: "Location deleted successfully",
      });
    },
    meta: {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]],
    },
  });
}
